import { useState, useEffect, useCallback } from 'react';
import { Spin, notification, Modal, Avatar } from 'antd';
import {
  BankOutlined, CheckCircleOutlined, CloseOutlined, EnvironmentOutlined,
  FilePdfOutlined, IdcardOutlined, LinkOutlined, MailOutlined,
  MessageOutlined, PhoneOutlined, SearchOutlined, ShopOutlined,
  TeamOutlined, TrophyOutlined, UserAddOutlined, UsergroupAddOutlined,
  WhatsAppOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import { baseUrl } from '../../../config';
import { M, G, BG } from '../shared/constants';
import { authHeader, useLang, countryFlag } from '../shared/utils';
import { NewsDetailModal, ImagePreviewModal } from '../components/NewsDetailModal';
import { LastSeenBadge } from '../components/PostCard';

const fmtDate = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return d; }
};

export function MyContactsTab({ profile, onSwitchTab }) {
  const [contacts,            setContacts]            = useState([]);
  const [sent,                setSent]                = useState([]);   // outgoing pending member-to-member
  const [received,            setReceived]            = useState([]);
  const [bizSent,             setBizSent]             = useState([]);   // outgoing biz contact requests
  const [bizReceivedAccepted, setBizReceivedAccepted] = useState([]);   // biz requests I accepted (as owner)
  const [loading,             setLoading]             = useState(true);
  const [responding,          setResponding]          = useState({});
  const [cancelling,          setCancelling]          = useState({});

  const ini = n => (n || '?').trim().split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [cRes, rRes, sRes, bizRRes, bizSRes] = await Promise.all([
        axios.get(`${baseUrl}/member/contacts`,                              { headers: authHeader() }),
        axios.get(`${baseUrl}/member/contact-requests`,                      { headers: authHeader() }),
        axios.get(`${baseUrl}/member/sent-contact-requests`,                 { headers: authHeader() }),
        axios.get(`${baseUrl}/member/biz-contact-requests/received`,         { headers: authHeader() }),
        axios.get(`${baseUrl}/member/biz-contact-requests/sent`,             { headers: authHeader() }),
      ]);
      if (cRes.data.status)   setContacts(cRes.data.result || []);
      if (bizSRes.data.status) setBizSent(bizSRes.data.result || []);

      // Pending received: member + biz (pending only for action)
      const memberReceived = rRes.data.status  ? (rRes.data.result  || []).map(r => ({ ...r, _type: 'member' })) : [];
      const bizPendingReceived = bizRRes.data.status ? (bizRRes.data.result || []).filter(r => r.status === 'pending').map(r => ({
        ...r,
        name:    r.requester_name,
        photo:   r.requester_photo,
        village: r.requester_village,
        district:r.requester_district,
        _type:   'biz',
        _biz_label: r.firm_name || r.biz_name,
      })) : [];
      setReceived([...memberReceived, ...bizPendingReceived]);

      // Accepted biz received (as owner) — show requester's phone
      const bizAccepted = bizRRes.data.status ? (bizRRes.data.result || []).filter(r => r.status === 'accepted') : [];
      setBizReceivedAccepted(bizAccepted);

      if (sRes.data.status)   setSent(sRes.data.result || []);
    } catch (err) {
      console.error('fetchAll error', err);
    } finally { setLoading(false); }
  }, []); // eslint-disable-line

  useEffect(() => { fetchAll(); }, []); // eslint-disable-line

  const respondRequest = async (req, action) => {
    const key = req._type === 'biz' ? `biz_${req.id}` : req.id;
    setResponding(r => ({ ...r, [key]: true }));
    try {
      const url = req._type === 'biz'
        ? `${baseUrl}/member/biz-contact-request/${req.id}`
        : `${baseUrl}/member/contact-request/${req.id}`;
      await axios.patch(url, { action }, { headers: authHeader() });
      notification.success({ message: action === 'accept' ? '✅ संपर्क जोड़ा गया!' : 'अस्वीकार किया गया' });
      fetchAll();
    } catch (err) {
      notification.error({ message: err.response?.data?.message || 'Error' });
    } finally { setResponding(r => ({ ...r, [key]: false })); }
  };

  const cancelRequest = async (reqId) => {
    setCancelling(c => ({ ...c, [reqId]: true }));
    try {
      await axios.delete(`${baseUrl}/member/contact-request/${reqId}`, { headers: authHeader() });
      notification.success({ message: 'अनुरोध रद्द किया गया' });
      setSent(prev => prev.filter(r => r.id !== reqId));
    } catch (err) {
      notification.error({ message: err.response?.data?.message || 'Error' });
    } finally { setCancelling(c => ({ ...c, [reqId]: false })); }
  };

  const ContactCard = ({ c, status }) => (
    <div style={{
      background: '#fff', borderRadius: 12,
      border: `1px solid ${status === 'accepted' ? '#e5e7eb' : status === 'pending' ? '#fde68a' : '#bfdbfe'}`,
      padding: '12px 14px', marginBottom: 10,
      display: 'flex', alignItems: 'center', gap: 12,
    }}>
      {c.photo
        ? <img src={c.photo} alt={c.name}
            onError={e => { e.target.style.display='none'; e.target.nextElementSibling.style.display='flex'; }}
            style={{ width: 48, height: 48, borderRadius: '50%', objectFit: 'cover', objectPosition: 'top',
              border: `2px solid ${status === 'accepted' ? M : '#fcd34d'}`, flexShrink: 0 }} />
        : null
      }
      <div style={{ display: c.photo ? 'none' : 'flex', width: 48, height: 48, borderRadius: '50%',
        background: status === 'accepted' ? M : '#fef3c7', flexShrink: 0,
        alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 800,
        color: status === 'accepted' ? G : '#92400e' }}>
        {ini(c.name)}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>{c.name}</div>
          {status === 'pending'  && <span style={{ fontSize: 9, background: '#fef3c7', color: '#92400e', borderRadius: 20, padding: '1px 7px', fontWeight: 700, display:'inline-flex', alignItems:'center', gap:3 }}><MailOutlined style={{fontSize:8}}/> प्रतीक्षारत</span>}
          {status === 'received' && <span style={{ fontSize: 9, background: '#dbeafe', color: '#1d4ed8', borderRadius: 20, padding: '1px 7px', fontWeight: 700, display:'inline-flex', alignItems:'center', gap:3 }}><UserAddOutlined style={{fontSize:8}}/> नया अनुरोध</span>}
          {status === 'accepted' && <LastSeenBadge ts={c.last_active_at} />}
        </div>
        {c.father_name && <div style={{ fontSize: 11, color: '#6b7280' }}>पिता: {c.father_name}</div>}
        <div style={{ fontSize: 11, color: '#9ca3af' }}>
          <EnvironmentOutlined style={{ marginRight: 3 }} />
          {[c.village, c.tahsil, c.district].filter(Boolean).join(', ')}
        </div>
      </div>
      <div style={{ textAlign: 'right', flexShrink: 0 }}>
        {status === 'accepted' && c.phone_no && (
          <a href={`tel:${c.phone_no}`} style={{
            display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 700,
            color: '#15803d', background: '#dcfce7', borderRadius: 6, padding: '4px 8px',
            textDecoration: 'none',
          }}>
            <PhoneOutlined /> {c.phone_no}
          </a>
        )}
        {status === 'received' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-end' }}>
            {c._type === 'biz' && (
              <div style={{ fontSize: 9, color: '#6b7280', marginBottom: 2, textAlign: 'right' }}>
                📋 {c._biz_label}
              </div>
            )}
            <div style={{ display: 'flex', gap: 5 }}>
              <button onClick={() => respondRequest(c, 'accept')}
                disabled={!!responding[c._type === 'biz' ? `biz_${c.id}` : c.id]}
                style={{ background: '#15803d', color: '#fff', border: 'none', borderRadius: 6,
                  padding: '5px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                ✓ स्वीकार
              </button>
              <button onClick={() => respondRequest(c, 'decline')}
                disabled={!!responding[c._type === 'biz' ? `biz_${c.id}` : c.id]}
                style={{ background: '#fff', color: '#dc2626', border: '1px solid #dc2626', borderRadius: 6,
                  padding: '5px 8px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                ✕
              </button>
            </div>
          </div>
        )}
        {status === 'pending' && (
          <span style={{ fontSize: 10, color: '#9ca3af' }}>स्वीकृति प्रतीक्षा में</span>
        )}
      </div>
    </div>
  );

  // WhatsApp invite — share referral link with anyone (members or non-members)
  const shareInvite = () => {
    const code       = profile?.membership_no || (profile?.id ? `ABRSVS#${profile.id}` : null);
    const senderName = profile?.name_hn || profile?.name || 'एक सदस्य';
    const portalUrl  = 'https://portal.brahmdhamtirth.org/user-portal-login';
    const codeBlock  = code ? `\n\n🔑 *रेफरल कोड:* *${code}*\n_( पंजीकरण में यह कोड डालें )_` : '';
    const msg =
`🙏 *जय गुरूमहाराज री सा* 🙏

*${senderName}* जी ने आपको ABRSVS समाज पोर्टल से जुड़ने का निमंत्रण दिया है।${codeBlock}

📲 *अभी जुड़ें:*
${portalUrl}`;
    if (navigator.share) {
      navigator.share({ title: 'ABRSVS समाज पोर्टल', text: msg }).catch(() => {
        window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
      });
    } else {
      window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
    }
  };

  if (loading) return <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>;

  const bizAccepted    = bizSent.filter(r => r.status === 'accepted');
  const bizPendingSent = bizSent.filter(r => r.status === 'pending');
  const hasAny = contacts.length || sent.length || received.length ||
                 bizSent.length || bizReceivedAccepted.length;

  return (
    <div>

      {/* ── TOP ACTION BAR ── */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
        <button onClick={shareInvite} style={{
          flex: 1, background: '#25d366', border: 'none', borderRadius: 10,
          padding: '10px 0', cursor: 'pointer', color: '#fff', fontWeight: 700, fontSize: 12,
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
        }}>
          <WhatsAppOutlined style={{ fontSize: 16 }} /> {profile ? 'दोस्तों को बुलाएं' : 'Invite via WhatsApp'}
        </button>
        {onSwitchTab && (
          <button onClick={() => onSwitchTab('directory')} style={{
            flex: 1, background: '#fff', border: `1.5px solid ${M}`, borderRadius: 10,
            padding: '10px 0', cursor: 'pointer', color: M, fontWeight: 700, fontSize: 12,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          }}>
            <SearchOutlined style={{ fontSize: 14 }} /> नए मित्र खोजें
          </button>
        )}
      </div>

      {/* ── Empty state ── */}
      {!hasAny && (
        <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>
          <TeamOutlined style={{ fontSize: 36, color: '#d1d5db', marginBottom: 10, display: 'block' }} />
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4, color: '#374151' }}>अभी कोई मित्र नहीं है</div>
          <div style={{ fontSize: 11, marginBottom: 16 }}>"नए मित्र खोजें" पर क्लिक करके सदस्यों को जोड़ें</div>
        </div>
      )}

      {/* ── RECEIVED REQUESTS (pending only) ── */}
      {received.length > 0 && (
        <>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#1d4ed8', marginBottom: 10,
            display: 'flex', alignItems: 'center', gap: 6 }}>
            <MailOutlined style={{ color: '#1d4ed8' }} />
            <span style={{ background: '#dbeafe', borderRadius: 20, padding: '3px 12px' }}>
              आए हुए अनुरोध ({received.length})
            </span>
          </div>
          {received.map(c => <ContactCard key={c.id} c={c} status="received" />)}
          <div style={{ height: 1, background: '#f0f0f0', margin: '14px 0' }} />
        </>
      )}

      {/* ── ACCEPTED MEMBER CONTACTS (FRIENDS) ── */}
      {contacts.length > 0 && (
        <>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#15803d', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
            <TeamOutlined style={{ color: '#15803d' }} />
            <span>मेरे मित्र ({contacts.length})</span>
          </div>
          {contacts.map((c, i) => <ContactCard key={i} c={c} status="accepted" />)}
        </>
      )}

      {/* ── BUSINESS CONTACTS (accepted sent + accepted received-as-owner) ── */}
      {(bizAccepted.length > 0 || bizReceivedAccepted.length > 0) && (
        <>
          <div style={{ height: 1, background: '#f0f0f0', margin: '14px 0' }} />
          <div style={{ fontSize: 12, fontWeight: 700, color: '#7c3aed', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
            <ShopOutlined style={{ color: '#7c3aed' }} /> व्यापार संपर्क ({bizAccepted.length + bizReceivedAccepted.length})
          </div>

          {/* Biz contacts I requested and they accepted */}
          {bizAccepted.map(r => (
            <div key={`bs-${r.id}`} style={{
              background: '#faf5ff', borderRadius: 12, border: '1px solid #e9d5ff',
              padding: '12px 14px', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 12,
            }}>
              {r.biz_logo
                ? <img src={r.biz_logo} alt={r.firm_name || r.biz_name}
                    onError={e => { e.target.style.display='none'; e.target.nextElementSibling.style.display='flex'; }}
                    style={{ width: 48, height: 48, borderRadius: 8, objectFit: 'cover',
                      border: '2px solid #c4b5fd', flexShrink: 0 }} />
                : null}
              <div style={{ display: r.biz_logo ? 'none' : 'flex', width: 48, height: 48, borderRadius: 8,
                background: '#ede9fe', flexShrink: 0, alignItems: 'center', justifyContent: 'center',
                fontSize: 22 }}><ShopOutlined style={{ color: '#7c3aed' }} /></div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>{r.firm_name || r.biz_name}</div>
                {r.owner_name && <div style={{ fontSize: 11, color: '#6b7280' }}>स्वामी: {r.owner_name}</div>}
                {(r.owner_village || r.owner_district) && (
                  <div style={{ fontSize: 11, color: '#9ca3af' }}>
                    <EnvironmentOutlined style={{ marginRight: 3 }} />
                    {[r.owner_village, r.owner_district].filter(Boolean).join(', ')}
                  </div>
                )}
                <div style={{ fontSize: 10, color: '#a78bfa', marginTop: 2 }}>मेरे द्वारा अनुरोधित</div>
              </div>
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                {r.biz_phone && (
                  <a href={`tel:${r.biz_phone}`} style={{
                    display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 700,
                    color: '#15803d', background: '#dcfce7', borderRadius: 6, padding: '4px 8px',
                    textDecoration: 'none', marginBottom: 4,
                  }}>
                    <PhoneOutlined /> {r.biz_phone}
                  </a>
                )}
              </div>
            </div>
          ))}

          {/* Biz contacts they requested from my business and I accepted (I know their phone) */}
          {bizReceivedAccepted.map(r => (
            <div key={`br-${r.id}`} style={{
              background: '#f0fdf4', borderRadius: 12, border: '1px solid #bbf7d0',
              padding: '12px 14px', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 12,
            }}>
              {r.requester_photo
                ? <img src={r.requester_photo} alt={r.requester_name}
                    onError={e => { e.target.style.display='none'; e.target.nextElementSibling.style.display='flex'; }}
                    style={{ width: 48, height: 48, borderRadius: '50%', objectFit: 'cover',
                      objectPosition: 'top', border: '2px solid #86efac', flexShrink: 0 }} />
                : null}
              <div style={{ display: r.requester_photo ? 'none' : 'flex', width: 48, height: 48, borderRadius: '50%',
                background: '#dcfce7', flexShrink: 0, alignItems: 'center', justifyContent: 'center',
                fontSize: 16, fontWeight: 800, color: '#15803d' }}>
                {ini(r.requester_name)}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>{r.requester_name}</div>
                {(r.requester_village || r.requester_district) && (
                  <div style={{ fontSize: 11, color: '#9ca3af' }}>
                    <EnvironmentOutlined style={{ marginRight: 3 }} />
                    {[r.requester_village, r.requester_district].filter(Boolean).join(', ')}
                  </div>
                )}
                <div style={{ fontSize: 10, color: '#16a34a', marginTop: 2 }}>
                  "{r.firm_name || r.biz_name}" के लिए मुझसे संपर्क किया
                </div>
              </div>
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                {r.requester_phone && (
                  <a href={`tel:${r.requester_phone}`} style={{
                    display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 700,
                    color: '#15803d', background: '#dcfce7', borderRadius: 6, padding: '4px 8px',
                    textDecoration: 'none',
                  }}>
                    <PhoneOutlined /> {r.requester_phone}
                  </a>
                )}
              </div>
            </div>
          ))}
        </>
      )}

      {/* ── SENT / OUTGOING MEMBER REQUESTS with full details ── */}
      {(sent.length > 0 || bizPendingSent.length > 0) && (
        <>
          {(contacts.length > 0 || bizAccepted.length > 0 || bizReceivedAccepted.length > 0) &&
            <div style={{ height: 1, background: '#f0f0f0', margin: '14px 0' }} />}
          <div style={{ fontSize: 12, fontWeight: 700, color: '#92400e', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
            <MailOutlined style={{ color: '#92400e' }} /> भेजे गए अनुरोध ({sent.length + bizPendingSent.length})
          </div>

          {/* Pending biz contact requests I sent */}
          {bizPendingSent.map(r => (
            <div key={`bsp-${r.id}`} style={{
              background: '#fff', border: '1px solid #fde68a',
              borderRadius: 12, padding: '12px 14px', marginBottom: 10,
              display: 'flex', alignItems: 'center', gap: 12,
            }}>
              <div style={{ width: 48, height: 48, borderRadius: 8, background: '#fef9c3',
                flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>
                🏪
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>{r.firm_name || r.biz_name}</span>
                  <span style={{ fontSize: 9, background: '#fef3c7', color: '#92400e',
                    borderRadius: 20, padding: '2px 8px', fontWeight: 700 }}>प्रतीक्षारत</span>
                </div>
                {r.owner_name && <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>स्वामी: {r.owner_name}</div>}
                <div style={{ fontSize: 10, color: '#d1d5db', marginTop: 2 }}>
                  {r.requested_at ? `भेजा: ${new Date(r.requested_at).toLocaleDateString('hi-IN')}` : ''}
                </div>
              </div>
              <span style={{ fontSize: 10, color: '#9ca3af', flexShrink: 0 }}>स्वीकृति प्रतीक्षा में</span>
            </div>
          ))}
          {sent.map(r => (
            <div key={r.id} style={{
              background: '#fff', border: '1px solid #fde68a',
              borderRadius: 12, padding: '12px 14px', marginBottom: 10,
              display: 'flex', alignItems: 'center', gap: 12,
            }}>
              {/* Avatar */}
              {r.photo
                ? <img src={r.photo} alt={r.name}
                    style={{ width: 50, height: 50, borderRadius: '50%', objectFit: 'cover',
                      objectPosition: 'top', border: '2px solid #fcd34d', flexShrink: 0 }} />
                : <div style={{ width: 50, height: 50, borderRadius: '50%', background: '#fef3c7',
                    flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 16, fontWeight: 800, color: '#92400e', border: '2px solid #fcd34d' }}>
                    {ini(r.name)}
                  </div>
              }

              {/* Details */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 13, fontWeight: 800, color: '#111827' }}>{r.name}</span>
                  <span style={{ fontSize: 9, background: '#fef3c7', color: '#92400e',
                    borderRadius: 20, padding: '2px 8px', fontWeight: 700 }}>प्रतीक्षारत</span>
                </div>
                {r.father_name && (
                  <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>
                    पिता: {r.father_name}
                  </div>
                )}
                {(r.village || r.district) && (
                  <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 1 }}>
                    <EnvironmentOutlined style={{ marginRight: 3, fontSize: 10 }} />
                    {[r.village, r.tahsil, r.district].filter(Boolean).join(', ')}
                  </div>
                )}
                <div style={{ fontSize: 10, color: '#d1d5db', marginTop: 2 }}>
                  {r.requested_at ? `भेजा: ${new Date(r.requested_at).toLocaleDateString('hi-IN')}` : ''}
                </div>
              </div>

              {/* Cancel button */}
              <button
                onClick={() => cancelRequest(r.id)}
                disabled={!!cancelling[r.id]}
                style={{
                  background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8,
                  padding: '6px 12px', fontSize: 11, fontWeight: 700, color: '#b91c1c',
                  cursor: 'pointer', flexShrink: 0, opacity: cancelling[r.id] ? 0.6 : 1,
                  display: 'flex', alignItems: 'center', gap: 4,
                }}>
                {cancelling[r.id] ? '...' : '✕ रद्द करें'}
              </button>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

// ── Announcements Tab (समाज समाचार) ──────────────────────────────────────────
export function AnnouncementsTab() {
  const [items, setItems]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  useEffect(() => {
    axios.get(`${baseUrl}/member/announcements?limit=20`, { headers: authHeader() })
      .then(r => { if (r.data.status) setItems(r.data.result || []); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line

  if (loading) return <div style={{ textAlign: 'center', padding: 48 }}><Spin /></div>;
  if (!items.length) return (
    <div style={{ textAlign: 'center', padding: 48, color: '#9ca3af' }}>
      <div style={{ fontSize: 36, marginBottom: 10 }}>📢</div>
      <div style={{ fontWeight: 700 }}>कोई सूचना नहीं है</div>
    </div>
  );
  return (
    <div>
      <div style={{ fontSize: 15, fontWeight: 800, color: M, marginBottom: 16 }}>📢 समाचार एवं घोषणाएं</div>
      {items.map(item => (
        <div key={item.id} style={{ background: '#fff', borderRadius: 14, border: '1px solid #e5e7eb', marginBottom: 12, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
          {item.image_url && (
            <img src={item.image_url} alt={item.title} onClick={() => setPreview(item.image_url)}
              style={{ display: 'block', width: '100%', maxHeight: 320, objectFit: 'cover', cursor: 'zoom-in' }} />
          )}
          <div style={{ padding: '12px 14px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 6 }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: '#111827' }}>{item.title}</div>
              {item.category_name && (
                <span style={{ fontSize: 10, fontWeight: 700, background: item.category_color || '#fee2e2', color: M, borderRadius: 20, padding: '2px 9px', flexShrink: 0 }}>
                  {item.category_name}
                </span>
              )}
            </div>
            {item.content && <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{item.content}</div>}
            <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 8 }}>
              {item.post_date ? new Date(item.post_date).toLocaleDateString('hi-IN', { day: 'numeric', month: 'long', year: 'numeric' }) : ''}
            </div>
          </div>
        </div>
      ))}
      <ImagePreviewModal src={preview} onClose={() => setPreview(null)} />
    </div>
  );
}

// ── Samaj Info Tab (समाज जानकारी) ────────────────────────────────────────────
export function SamajTab() {
  const [activeSection, setActiveSection] = useState('transparency');
  const [transparency, setTransparency]   = useState(null);
  const [scholarships, setScholarships]   = useState([]);
  const [achievements, setAchievements]   = useState([]);
  const [loading, setLoading]             = useState(true);
  const year = new Date().getFullYear();

  // Expenditure state
  const [expData,     setExpData]     = useState(null);
  const [expLoading,  setExpLoading]  = useState(false);
  const [selectedFY,  setSelectedFY]  = useState('');
  const [expPage,     setExpPage]     = useState(1);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      axios.get(`${baseUrl}/member/transparency?year=${year}`, { headers: authHeader() }),
      axios.get(`${baseUrl}/member/scholarships`,             { headers: authHeader() }),
      axios.get(`${baseUrl}/member/achievements`,             { headers: authHeader() }),
    ]).then(([t, s, a]) => {
      if (t.data.status) setTransparency(t.data);
      if (s.data.status) setScholarships(s.data.result || []);
      if (a.data.status) setAchievements(a.data.result || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []); // eslint-disable-line

  // Load expenditures when section opens or FY/page changes
  useEffect(() => {
    if (activeSection !== 'expenditure') return;
    setExpLoading(true);
    const q = selectedFY ? `&fy=${encodeURIComponent(selectedFY)}` : '';
    axios.get(`${baseUrl}/member/expenditure-public?page=${expPage}${q}`, { headers: authHeader() })
      .then(r => { if (r.data.status) setExpData(r.data); })
      .catch(() => {}).finally(() => setExpLoading(false));
  }, [activeSection, selectedFY, expPage]); // eslint-disable-line

  const CAT_COLORS = {
    'Education': '#1d4ed8', 'Scholarship': '#7c3aed', 'Construction': '#b45309',
    'Event': '#0f766e', 'Medical': '#be123c', 'Legal': '#374151', 'Other': '#6b7280',
  };

  const sections = [
    { key: 'transparency', label: 'पारदर्शिता',  icon: <BankOutlined /> },
    { key: 'expenditure',  label: 'व्यय विवरण',  icon: <FilePdfOutlined /> },
    { key: 'scholarships', label: 'लाभार्थी',    icon: <IdcardOutlined /> },
    { key: 'achievements', label: 'उपलब्धियां',  icon: <TrophyOutlined /> },
  ];

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 16, background: '#fff', borderRadius: 10, padding: 5, border: '1px solid #e5e7eb', width: 'fit-content' }}>
        {sections.map(s => (
          <button key={s.key} onClick={() => setActiveSection(s.key)} style={{
            fontSize: 11, fontWeight: 700, padding: '6px 12px', borderRadius: 8, cursor: 'pointer', border: 'none',
            background: activeSection === s.key ? M : 'transparent',
            color:      activeSection === s.key ? G : '#6b7280',
            display: 'flex', alignItems: 'center', gap: 5,
          }}>{s.icon}{s.label}</button>
        ))}
      </div>

      {loading ? <div style={{ textAlign: 'center', padding: 48 }}><Spin /></div> : (
        <>
          {/* Transparency */}
          {activeSection === 'transparency' && transparency && (
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 12 }}>वर्ष {year} — समाज खाता</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
                {[
                  { label: 'कुल संग्रह', value: `₹${Number(transparency.collection?.total || 0).toLocaleString('en-IN')}`, sub: `${transparency.collection?.count || 0} भुगतान`, color: '#15803D', bg: '#F0FDF4' },
                  { label: 'कुल व्यय',   value: `₹${Number(transparency.expenditure?.total || 0).toLocaleString('en-IN')}`, sub: `${transparency.expenditure?.count || 0} entries`, color: '#B91C1C', bg: '#FFF1F2' },
                ].map(c => (
                  <div key={c.label} style={{ background: c.bg, borderRadius: 12, padding: '14px 12px', textAlign: 'center' }}>
                    <div style={{ fontSize: 10, color: '#6b7280', fontWeight: 700, textTransform: 'uppercase', marginBottom: 4 }}>{c.label}</div>
                    <div style={{ fontSize: 22, fontWeight: 900, color: c.color }}>{c.value}</div>
                    <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 2 }}>{c.sub}</div>
                  </div>
                ))}
              </div>
              {/* Monthly bar chart (simple) */}
              {transparency.monthly?.length > 0 && (
                <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', padding: 14 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#374151', marginBottom: 10 }}>माहवार संग्रह</div>
                  {(() => {
                    const max = Math.max(...transparency.monthly.map(m => Number(m.collected)));
                    const months = ['जन','फर','मार','अप','मई','जून','जुल','अग','सित','अक्त','नव','दिस'];
                    return (
                      <div style={{ display: 'flex', gap: 4, alignItems: 'flex-end', height: 60 }}>
                        {transparency.monthly.map(m => (
                          <div key={m.month} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                            <div style={{ width: '100%', background: M, borderRadius: '3px 3px 0 0', height: `${Math.max(4, (Number(m.collected)/max)*50)}px` }} />
                            <div style={{ fontSize: 8, color: '#9ca3af' }}>{months[m.month-1]}</div>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
          )}

          {/* ── व्यय विवरण ── */}
          {activeSection === 'expenditure' && (
            <div>
              {/* Header + FY selector */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 800, color: '#111827' }}>व्यय विवरण</div>
                  <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>समाज शिक्षा निधि — Approved Expenditures</div>
                </div>
                {expData?.fy_list?.length > 0 && (
                  <select value={selectedFY} onChange={e => { setSelectedFY(e.target.value); setExpPage(1); }}
                    style={{ border: '1px solid #d1d5db', borderRadius: 8, padding: '6px 10px', fontSize: 12, color: '#374151', cursor: 'pointer' }}>
                    <option value="">All FY</option>
                    {expData.fy_list.map(f => (
                      <option key={f.fiscal_year} value={f.fiscal_year}>
                        {f.fiscal_year} — ₹{Number(f.total).toLocaleString('en-IN')}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              {expLoading ? (
                <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
              ) : expData ? (
                <>
                  {/* FY summary cards */}
                  {expData.fy_list?.length > 0 && !selectedFY && (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 8, marginBottom: 16 }}>
                      {expData.fy_list.slice(0, 4).map(f => (
                        <div key={f.fiscal_year} onClick={() => { setSelectedFY(f.fiscal_year); setExpPage(1); }}
                          style={{ background: '#fff', borderRadius: 10, border: `1.5px solid ${M}30`, padding: '10px 12px', cursor: 'pointer', textAlign: 'center' }}
                          onMouseEnter={e => e.currentTarget.style.borderColor = M}
                          onMouseLeave={e => e.currentTarget.style.borderColor = `${M}30`}>
                          <div style={{ fontSize: 10, color: '#6b7280', fontWeight: 700 }}>FY {f.fiscal_year}</div>
                          <div style={{ fontSize: 16, fontWeight: 900, color: M, marginTop: 2 }}>₹{Number(f.total).toLocaleString('en-IN')}</div>
                          <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 1 }}>{f.count} entries</div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Category breakdown */}
                  {expData.category_breakdown?.length > 0 && (
                    <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', padding: 14, marginBottom: 14 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 10 }}>
                        श्रेणीवार व्यय {selectedFY ? `— FY ${selectedFY}` : ''}
                      </div>
                      {expData.category_breakdown.map(cat => {
                        const total = expData.category_breakdown.reduce((s, c) => s + Number(c.total), 0);
                        const pct   = total > 0 ? Math.round((Number(cat.total) / total) * 100) : 0;
                        const color = CAT_COLORS[cat.category] || CAT_COLORS['Other'];
                        return (
                          <div key={cat.category} style={{ marginBottom: 10 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                              <div style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>{cat.category || 'अन्य'}</div>
                              <div style={{ fontSize: 12, fontWeight: 700, color }}>₹{Number(cat.total).toLocaleString('en-IN')} <span style={{ color: '#9ca3af', fontWeight: 400 }}>({pct}%)</span></div>
                            </div>
                            <div style={{ height: 6, background: '#f3f4f6', borderRadius: 4, overflow: 'hidden' }}>
                              <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 4, transition: 'width 0.6s ease' }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Expenditure list */}
                  {expData.result?.length > 0 ? (
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 10 }}>
                        विवरण सूची {selectedFY ? `— ${selectedFY}` : ''} ({expData.total})
                      </div>
                      {expData.result.map((e, i) => (
                        <div key={e.id} style={{ background: '#fff', borderRadius: 10, border: '1px solid #e5e7eb', padding: '12px 14px', marginBottom: 8 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <div style={{ flex: 1, minWidth: 0, marginRight: 10 }}>
                              <div style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>{e.purpose_name || 'सामान्य व्यय'}</div>
                              {e.beneficiary_name && <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>{e.beneficiary_name} {e.beneficiary_type ? `• ${e.beneficiary_type}` : ''}</div>}
                              <div style={{ display: 'flex', gap: 8, marginTop: 4, flexWrap: 'wrap' }}>
                                {e.purpose_category && (
                                  <span style={{ fontSize: 10, background: `${CAT_COLORS[e.purpose_category] || '#6b7280'}15`, color: CAT_COLORS[e.purpose_category] || '#6b7280', borderRadius: 20, padding: '1px 8px', fontWeight: 600 }}>
                                    {e.purpose_category}
                                  </span>
                                )}
                                {e.txn_date && <span style={{ fontSize: 10, color: '#9ca3af' }}>{new Date(e.txn_date).toLocaleDateString('hi-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>}
                                {e.payment_mode && <span style={{ fontSize: 10, color: '#9ca3af' }}>{e.payment_mode}</span>}
                              </div>
                              {e.remarks && <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4, fontStyle: 'italic' }}>"{e.remarks}"</div>}
                            </div>
                            <div style={{ textAlign: 'right', flexShrink: 0 }}>
                              <div style={{ fontSize: 15, fontWeight: 900, color: '#b91c1c' }}>₹{Number(e.amount).toLocaleString('en-IN')}</div>
                              <div style={{ fontSize: 9, color: '#9ca3af', marginTop: 2 }}>{e.fiscal_year}</div>
                            </div>
                          </div>
                        </div>
                      ))}

                      {/* Pagination */}
                      {expData.total > expData.limit && (
                        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 14 }}>
                          <button onClick={() => setExpPage(p => Math.max(1, p - 1))} disabled={expPage === 1}
                            style={{ background: expPage === 1 ? '#f3f4f6' : M, color: expPage === 1 ? '#9ca3af' : G, border: 'none', borderRadius: 8, padding: '7px 14px', fontSize: 12, fontWeight: 700, cursor: expPage === 1 ? 'default' : 'pointer' }}>
                            ← पिछला
                          </button>
                          <span style={{ fontSize: 12, color: '#6b7280', alignSelf: 'center' }}>
                            {expPage} / {Math.ceil(expData.total / expData.limit)}
                          </span>
                          <button onClick={() => setExpPage(p => p + 1)} disabled={expPage >= Math.ceil(expData.total / expData.limit)}
                            style={{ background: expPage >= Math.ceil(expData.total / expData.limit) ? '#f3f4f6' : M, color: expPage >= Math.ceil(expData.total / expData.limit) ? '#9ca3af' : G, border: 'none', borderRadius: 8, padding: '7px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                            अगला →
                          </button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div style={{ textAlign: 'center', padding: 32, color: '#9ca3af', fontSize: 12 }}>
                      {selectedFY ? `FY ${selectedFY} में कोई व्यय नहीं मिला` : 'कोई व्यय विवरण उपलब्ध नहीं'}
                    </div>
                  )}
                </>
              ) : null}
            </div>
          )}

          {/* Scholarships */}
          {activeSection === 'scholarships' && (
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}><IdcardOutlined /> समाज लाभार्थी ({scholarships.length})</div>
              {!scholarships.length ? (
                <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>कोई जानकारी उपलब्ध नहीं</div>
              ) : scholarships.map(s => (
                <div key={s.id} style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', padding: '12px 14px', marginBottom: 10, display: 'flex', gap: 10, alignItems: 'center' }}>
                  <div style={{ width: 40, height: 40, borderRadius: '50%', background: `${M}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0 }}>🎓</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>{s.name}</div>
                    <div style={{ fontSize: 11, color: '#6b7280' }}>{s.benefit_type || ''} {s.village ? `• ${s.village}` : ''}</div>
                  </div>
                  {s.amount && <div style={{ fontSize: 14, fontWeight: 800, color: '#15803D' }}>₹{Number(s.amount).toLocaleString('en-IN')}</div>}
                </div>
              ))}
            </div>
          )}

          {/* Achievements */}
          {activeSection === 'achievements' && (
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 12 }}>🏆 समाज गौरव ({achievements.length})</div>
              {!achievements.length ? (
                <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>कोई जानकारी उपलब्ध नहीं</div>
              ) : achievements.map(a => (
                <div key={a.id} style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', padding: '12px 14px', marginBottom: 10, display: 'flex', gap: 12, alignItems: 'center' }}>
                  {a.student_photo
                    ? <img src={a.student_photo} alt={a.student_name} style={{ width: 48, height: 48, borderRadius: '50%', objectFit: 'cover', border: `2px solid ${G}`, flexShrink: 0 }} />
                    : <div style={{ width: 48, height: 48, borderRadius: '50%', background: M, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>🏆</div>
                  }
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 800, color: '#111827' }}>{a.student_name}</div>
                    <div style={{ fontSize: 11, color: '#6b7280' }}>{a.exam_name} {a.exam_year ? `• ${a.exam_year}` : ''}</div>
                    {a.village && <div style={{ fontSize: 10, color: '#9ca3af' }}><EnvironmentOutlined style={{ marginRight: 3 }} />{a.village}</div>}
                  </div>
                  {a.rank_position && <div style={{ fontSize: 18, fontWeight: 900, color: M }}>#{a.rank_position}</div>}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

